import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { useCartData } from "../../../providers/cartDataStore";

import DeliveryTimeContent from "./DeliveryTimeContent";
import AddressSelectionModal from "../../../parhato2/src/entities/AddressSelectionModal";
import ProgressBarButton from "./ProgressBarButton";
import { ReactComponent as PencilIcon } from "../../../assets/icon/pencil.svg";
import { getCustomerFullAddress, getEnabledShippingMethods } from "../../../utils";
import { ShippingMethod } from "@core/types";
import { useOrderData } from "../../../providers/orderDataStore";
import { useConfig } from "../../../providers/config";
import useGetCurrentAddress from "../../../hooks/useGetCurrentAddress";
import { useAuth } from "../../../contexts/auth-context";

import { AddToOrderProductShelf } from "../../../parhato2/src/entities";
import { CustomBottomSheetRef } from "../../../components/UI/CustomBottomSheet";
import BonusPoints from "./BonusPoints";
import { useTranslation } from "react-i18next";
import { CartFormValues } from "./types";
import { useHistory } from "react-router";

import { PaymentTabs } from "./PaymentTabs";
import { CouponBottomSheet } from "./CouponBottomSheet";

import { CartProducts } from "./CartProducts";
import { LeaveAtDoorToggle } from "./LeaveAtDoorToggle";
import { TotalWeightAndPrice } from "./TotalWeightAndPrice";
import { CommentBottomSheet } from "./CommentBottomSheet";
import { ShippingCost } from "./ShippingCost";
import { CourierServiceToggle } from "./CourierServiceToggle";
import { CartTotal } from "./CartTotal";
import { useCustomerAddressesStorageData } from "../../../providers/customerAddressesStorage";
import { useTaxiShipping } from "./useTaxiShipping";
import { PaymentMethodIdEnum } from "@core/types";
import { CashGivenBottomSheet } from "./CashGivenBottomSheet";
import { useTaxiMasterShippingActions } from "../../../hooks";
import { OrderProgressText } from "./OrderProgressText";
import { useGetDeliveryShippingFeePerKg } from "../../../hooks/useGetDeliveryShippingFeePerKg";
import { useGetRequiredToPlaceOrderMessages } from "./useGetRequiredToPlaceOrderMessages";

interface CartFormProps {
  onProceedOrder: () => void;
  isFormValid: () => boolean;
  shippingMethods: ShippingMethod[];
}

export const CartForm = ({ onProceedOrder, isFormValid, shippingMethods }: CartFormProps) => {
  const config = useConfig();
  const { user } = useAuth();
  const history = useHistory();
  const { t } = useTranslation();
  const currentAddress = useGetCurrentAddress();
  const [shakePayment, setShakePayment] = useState(false);
  const { currentShippingAddressId } = useCustomerAddressesStorageData();

  const addressRef = useRef<CustomBottomSheetRef>(null);
  const deliveryTimeRef = useRef<CustomBottomSheetRef>(null);

  const { firstName, lastName, customerNote, paymentMethod, deliveryInterval, cash_given } = useOrderData();
  const isCashPayment = paymentMethod?.type === PaymentMethodIdEnum.cash;

  const enabledShippingMethods = getEnabledShippingMethods(shippingMethods);

  const { isTaxiMasterTurned, isTaxiMasterAvailable } = useTaxiMasterShippingActions({ enabledShippingMethods });

  const { setValue, control, handleSubmit } = useForm<CartFormValues>({
    mode: "onChange",
    defaultValues: {
      phoneNumber: user?.phone ?? "",
      firstName: firstName,
      lastName: lastName,
      comment: customerNote,
      coupon: "",
      point: "",
      payment: paymentMethod?.title ?? "Наличными",
      deliveryTime: deliveryInterval,
      leaveAtDoor: false,
      courierService: isTaxiMasterTurned && !!currentShippingAddressId,
      cash_given: cash_given ?? null,

      deliveryAddress:
        currentAddress?.fullAddress ??
        getCustomerFullAddress({
          city: currentAddress?.city,
          house: currentAddress?.house,
          isFlat: currentAddress?.isFlat,
          street: currentAddress?.street,
          apartmentNumber: currentAddress?.apartmentNumber,
        }),
    },
  });

  const { handleCalculateTaxiShipping, isCalculateTaxiShippingPending } = useTaxiShipping({
    setValue,
    enabledShippingMethods,
  });

  const { getRequiredToPlaceOrderMessages } = useGetRequiredToPlaceOrderMessages({ isTaxiMasterTurned });

  const requiredToPlaceOrderMessages = getRequiredToPlaceOrderMessages();
  const progressBarButtonText =
    requiredToPlaceOrderMessages.length > 0 ? requiredToPlaceOrderMessages[0].message : t("SendOrder");

  const feePerKg = useGetDeliveryShippingFeePerKg();

  const handleProgressBarClick = () => {
    const requiredToPlaceOrderMessages = getRequiredToPlaceOrderMessages();
    if (requiredToPlaceOrderMessages.length === 0) return;

    const firstMissingField = requiredToPlaceOrderMessages[0];

    // Open corresponding bottom sheet based on the missing field
    if (firstMissingField.code === "select_delivery_address") {
      addressRef.current?.open();
    } else if (
      firstMissingField.code === "choose_delivery_time" ||
      firstMissingField.code === "express_delivery_weight_exceeded"
    ) {
      deliveryTimeRef.current?.open();
    } else if (firstMissingField.code === "select_payment_method") {
      setShakePayment(true);
      setTimeout(() => setShakePayment(false), 400);
    } else if (firstMissingField.code === "taxi_delivery_cost_not_calculated") {
      if (currentAddress) {
        handleCalculateTaxiShipping({ isTurned: true, address: currentAddress });
      }
    }
  };

  const handleSubmitOrder = () => {
    if (isFormValid()) {
      onProceedOrder();
      return;
    }

    handleProgressBarClick();
  };

  const handleAddressAuthRequired = () => {
    history.push("/auth", { from: "/cart" });
  };

  return (
    <form
      onSubmit={handleSubmit(handleSubmitOrder)}
      className="w-full flex flex-col justify-between py-2 pt-[0.25rem] gap-6 mb-[env(safe-area-inset-bottom)] pb-24"
    >
      <div className="w-full flex flex-col">
        <AddressSelectionModal
          ref={addressRef}
          onAuthRequired={handleAddressAuthRequired}
          customBottomClassname="w-full min-h-[4.375rem] flex items-center justify-between px-2.5 py-4 border-b border-solid border-b-[#e5e7eb]"
          customPreview={(address) => (
            <>
              <div className="flex items-center gap-1 w-[95%]">
                <div className="truncate text-[#292929]">{address}</div>
                <div className="text-red-500">*</div>
              </div>
              <PencilIcon className="text-sm font-bold w-4 h-4 text-[#D5D5D5]" />
            </>
          )}
        />

        <CommentBottomSheet control={control} />

        <LeaveAtDoorToggle control={control} />

        <CartProducts />

        <TotalWeightAndPrice isTaxiMasterTurned={isTaxiMasterTurned} />

        {isTaxiMasterAvailable ? (
          <CourierServiceToggle
            control={control}
            handleCalculateTaxiShipping={handleCalculateTaxiShipping}
            isCalculateTaxiShippingPending={isCalculateTaxiShippingPending}
          />
        ) : (
          <ShippingCost />
        )}

        {/* // * Показывается только если такси мастер выключен */}
        {!isTaxiMasterTurned && <DeliveryTimeContent ref={deliveryTimeRef} />}

        <CouponBottomSheet control={control} setValue={setValue} />

        <BonusPoints control={control} />

        <PaymentTabs control={control} shakePayment={shakePayment} />
        {config.isCashGivenEnabled && isCashPayment && <CashGivenBottomSheet control={control} />}

        <OrderProgressText enabledShippingMethods={enabledShippingMethods} />
      </div>

      <AddToOrderProductShelf />

      <div className="w-full mx-auto flex flex-col items-center gap-6">
        <CartTotal />
        <ProgressBarButton buttonText={progressBarButtonText} type="submit" />
      </div>
    </form>
  );
};
